import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';

const TIPOS = [
  { label: 'Barbería / Peluquería',       value: 'barberia',      duracion: 30 },
  { label: 'Estética / Uñas',             value: 'estetica',      duracion: 30 },
  { label: 'Cancha de fútbol',            value: 'cancha_futbol', duracion: 60 },
  { label: 'Consultorio médico',          value: 'consultorio',   duracion: 20 },
  { label: 'Gimnasio / Personal Trainer', value: 'gimnasio',      duracion: 60 },
  { label: 'Otro',                        value: 'otro',          duracion: 30 },
];

const PLANES  = ['Individual', 'Intermedio', 'Profesional'];
const TAMANOS = ['F5', 'F6', 'F7', 'F8', 'F9', 'F11'];

const EMPTY_FORM = {
  nombre: '', slug: '', emoji: '📅',
  tipo_negocio: '', duracion_turno: 30,
  plan: 'Individual', activo: true,
};

const EMPTY_CANCHA = () => ({ nombre: '', tamano: 'F5' });
const EMPTY_COMBO  = () => ({ nombre: '', canchaIndices: [] });

function slugify(text) {
  return text.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

export default function AdminPanel() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('tenants'); // 'tenants' | 'usuarios'

  const [tenants, setTenants]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  // Estado para canchas (solo cuando tipo = cancha_futbol)
  const [canchas,           setCanchas]           = useState([EMPTY_CANCHA()]);
  const [tieneCombinaciones,setTieneCombinaciones]= useState(false);
  const [combinaciones,     setCombinaciones]     = useState([EMPTY_COMBO()]);

  // Estado para usuarios
  const [usuarios,        setUsuarios]        = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [assigningId,     setAssigningId]     = useState(null); // profile.id en edición
  const [assignTenantId,  setAssignTenantId]  = useState('');
  const [assignSaving,    setAssignSaving]    = useState(false);

  // Estado para crear usuario
  const [showCreateUser,   setShowCreateUser]   = useState(false);
  const [createUserForm,   setCreateUserForm]   = useState({ email: '', password: '', nombre: '', rol: 'user' });
  const [createUserError,  setCreateUserError]  = useState('');
  const [createUserSaving, setCreateUserSaving] = useState(false);

  // Estado para eliminar usuario
  const [deletingId,   setDeletingId]   = useState(null);
  const [deleteError,  setDeleteError]  = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Estado para eliminar tenant
  const [deletingTenantId,   setDeletingTenantId]   = useState(null);
  const [deleteTenantError,  setDeleteTenantError]  = useState('');
  const [deleteTenantSaving, setDeleteTenantSaving] = useState(false);

  // Filtro usuarios
  const [filtroTenant, setFiltroTenant] = useState('todos');

  useEffect(() => { loadTenants(); }, []);
  useEffect(() => { if (activeTab === 'usuarios') loadUsuarios(); }, [activeTab]);

  async function loadTenants() {
    setLoading(true);
    const { data } = await supabase
      .from('tenants').select('*').order('created_at', { ascending: false });
    setTenants(data || []);
    setLoading(false);
  }

  async function loadUsuarios() {
    setLoadingUsuarios(true);
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    setUsuarios(data || []);
    setLoadingUsuarios(false);
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    setCreateUserError('');
    const { email, password, nombre, rol } = createUserForm;
    if (!email || !password || !nombre) {
      return setCreateUserError('Email, contraseña y nombre son obligatorios.');
    }
    if (password.length < 6) {
      return setCreateUserError('La contraseña debe tener al menos 6 caracteres.');
    }
    setCreateUserSaving(true);
    const { error } = await supabase.functions.invoke('create-user', {
      body: { email, password, nombre, rol },
    });
    setCreateUserSaving(false);
    if (error) return setCreateUserError(error.message || 'Error al crear usuario.');
    setCreateUserForm({ email: '', password: '', nombre: '', rol: 'user' });
    setShowCreateUser(false);
    loadUsuarios();
  }

  async function handleAssign(profileId) {
    setAssignSaving(true);
    // Desasignar tenant anterior de este usuario (si tenía)
    await supabase.from('tenants').update({ user_id: null }).eq('user_id', profileId);
    // Asignar nuevo tenant
    if (assignTenantId) {
      await supabase.from('tenants').update({ user_id: profileId }).eq('id', assignTenantId);
    }
    setAssignSaving(false);
    setAssigningId(null);
    setAssignTenantId('');
    loadTenants();
    loadUsuarios();
  }

  async function handleDeleteUser(userId) {
    setDeleteSaving(true);
    setDeleteError('');
    const { error } = await supabase.functions.invoke('delete-user', {
      body: { user_id: userId },
    });
    setDeleteSaving(false);
    if (error) {
      setDeleteError(error.message || 'Error al eliminar usuario.');
      return;
    }
    setDeletingId(null);
    loadTenants();
    loadUsuarios();
  }

  async function handleDeleteTenant(tenantId) {
    setDeleteTenantSaving(true);
    setDeleteTenantError('');
    // Obtener combinaciones del tenant para borrar sus junctions
    const { data: combos } = await supabase
      .from('combinaciones').select('id').eq('tenant_id', tenantId);
    if (combos?.length) {
      const comboIds = combos.map(c => c.id);
      await supabase.from('combinacion_canchas').delete().in('combinacion_id', comboIds);
      await supabase.from('combinaciones').delete().eq('tenant_id', tenantId);
    }
    await supabase.from('canchas').delete().eq('tenant_id', tenantId);
    await supabase.from('tenants').update({ user_id: null }).eq('id', tenantId);
    const { error } = await supabase.from('tenants').delete().eq('id', tenantId);
    setDeleteTenantSaving(false);
    if (error) {
      setDeleteTenantError(error.message || 'Error al eliminar tenant.');
      return;
    }
    setDeletingTenantId(null);
    loadTenants();
  }

  function handleNombreChange(nombre) {
    setForm(f => ({ ...f, nombre, slug: slugify(nombre) }));
  }

  function handleTipoChange(tipo_negocio) {
    const tipo = TIPOS.find(t => t.value === tipo_negocio);
    setForm(f => ({ ...f, tipo_negocio, duracion_turno: tipo ? tipo.duracion : 30 }));
    // Reset cancha state al cambiar tipo
    setCanchas([EMPTY_CANCHA()]);
    setTieneCombinaciones(false);
    setCombinaciones([EMPTY_COMBO()]);
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setCanchas([EMPTY_CANCHA()]);
    setTieneCombinaciones(false);
    setCombinaciones([EMPTY_COMBO()]);
    setError('');
  }

  // ─── Helpers para canchas ────────────────────────────────────────────────────
  function addCancha()     { setCanchas(c => [...c, EMPTY_CANCHA()]); }
  function removeCancha(i) {
    setCanchas(c => c.filter((_, idx) => idx !== i));
    // Limpiar referencias en combinaciones
    setCombinaciones(combos => combos.map(cb => ({
      ...cb,
      canchaIndices: cb.canchaIndices
        .filter(idx => idx !== i)
        .map(idx => idx > i ? idx - 1 : idx),
    })));
  }
  function updateCancha(i, field, value) {
    setCanchas(c => c.map((cancha, idx) => idx === i ? { ...cancha, [field]: value } : cancha));
  }

  // ─── Helpers para combinaciones ──────────────────────────────────────────────
  function addCombinacion()     { setCombinaciones(c => [...c, EMPTY_COMBO()]); }
  function removeCombinacion(i) { setCombinaciones(c => c.filter((_, idx) => idx !== i)); }
  function updateComboNombre(i, nombre) {
    setCombinaciones(c => c.map((cb, idx) => idx === i ? { ...cb, nombre } : cb));
  }
  function toggleCanchaEnCombo(comboIdx, canchaIdx) {
    setCombinaciones(combos => combos.map((cb, i) => {
      if (i !== comboIdx) return cb;
      const has = cb.canchaIndices.includes(canchaIdx);
      return {
        ...cb,
        canchaIndices: has
          ? cb.canchaIndices.filter(x => x !== canchaIdx)
          : [...cb.canchaIndices, canchaIdx],
      };
    }));
  }

  // ─── Submit ──────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.nombre || !form.slug) return setError('Nombre y slug son obligatorios.');

    if (form.tipo_negocio === 'cancha_futbol') {
      const canchasValidas = canchas.filter(c => c.nombre.trim());
      if (canchasValidas.length === 0) return setError('Agregá al menos una cancha.');
      if (tieneCombinaciones) {
        const combosValidas = combinaciones.filter(cb => cb.canchaIndices.length >= 2);
        if (combosValidas.length === 0) return setError('Cada combinación debe incluir al menos 2 canchas.');
      }
    }

    setSaving(true);

    // 1. Insertar tenant
    const { data: tenantData, error: tenantErr } = await supabase
      .from('tenants')
      .insert([{
        nombre:         form.nombre,
        slug:           form.slug,
        emoji:          form.emoji || '📅',
        tipo_negocio:   form.tipo_negocio || null,
        duracion_turno: Number(form.duracion_turno),
        plan:           form.plan,
        activo:         form.activo,
      }])
      .select('id')
      .single();

    if (tenantErr) { setSaving(false); return setError(tenantErr.message); }

    const tenantId = tenantData.id;

    // 2. Si es cancha de fútbol, insertar canchas + combinaciones
    if (form.tipo_negocio === 'cancha_futbol') {
      const canchasValidas = canchas.filter(c => c.nombre.trim());

      const { data: canchasData, error: canchasErr } = await supabase
        .from('canchas')
        .insert(canchasValidas.map(c => ({ tenant_id: tenantId, nombre: c.nombre, tamano: c.tamano })))
        .select('id');

      if (canchasErr) { setSaving(false); return setError(canchasErr.message); }

      // Mapa: índice original (en canchas[]) → ID real
      const validIndices = canchas.reduce((acc, c, i) => {
        if (c.nombre.trim()) acc.push(i); return acc;
      }, []);
      const indexToId = {};
      validIndices.forEach((origIdx, pos) => { indexToId[origIdx] = canchasData[pos].id; });

      if (tieneCombinaciones) {
        const combosValidas = combinaciones.filter(cb => cb.canchaIndices.length >= 2);

        for (const combo of combosValidas) {
          const { data: comboData, error: comboErr } = await supabase
            .from('combinaciones')
            .insert([{ tenant_id: tenantId, nombre: combo.nombre || null }])
            .select('id')
            .single();

          if (comboErr) { setSaving(false); return setError(comboErr.message); }

          const junctions = combo.canchaIndices
            .filter(idx => indexToId[idx])
            .map(idx => ({ combinacion_id: comboData.id, cancha_id: indexToId[idx] }));

          if (junctions.length > 0) {
            const { error: jErr } = await supabase.from('combinacion_canchas').insert(junctions);
            if (jErr) { setSaving(false); return setError(jErr.message); }
          }
        }
      }
    }

    setSaving(false);
    setShowForm(false);
    resetForm();
    loadTenants();
  }

  const esCancha = form.tipo_negocio === 'cancha_futbol';

  const visibleUsuarios = usuarios
    .filter(u => u.id !== currentUser?.id)
    .filter(u => {
      const tieneTenant = tenants.some(t => t.user_id === u.id);
      if (filtroTenant === 'con-tenant') return tieneTenant;
      if (filtroTenant === 'sin-tenant') return !tieneTenant;
      return true;
    });

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Tabs internos */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {[{ id:'tenants', label:'Tenants' }, { id:'usuarios', label:'Usuarios' }].map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setShowForm(false); resetForm(); }}
            style={{
              padding:'7px 18px', borderRadius:'var(--r-full)',
              border: activeTab === tab.id ? '2px solid var(--green)' : '1px solid var(--border)',
              background: activeTab === tab.id ? 'var(--green-bg)' : 'transparent',
              color: activeTab === tab.id ? 'var(--green-light)' : 'var(--text-secondary)',
              fontFamily:'var(--font-body)', fontSize:13, fontWeight:600, cursor:'pointer',
              transition:'all .15s',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── Tab: Usuarios ─────────────────────────────────────────────────── */}
      {activeTab === 'usuarios' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
              USUARIOS ({visibleUsuarios.length})
            </div>
            <button
              onClick={() => { setShowCreateUser(s => !s); setCreateUserError(''); setCreateUserForm({ email: '', password: '', nombre: '', rol: 'user' }); }}
              style={{
                padding:'8px 18px', borderRadius:'var(--r-full)',
                background:'var(--green)', color:'#fff', border:'none',
                fontFamily:'var(--font-body)', fontSize:13, fontWeight:600, cursor:'pointer',
              }}
            >
              {showCreateUser ? 'Cancelar' : '+ Nuevo Usuario'}
            </button>
          </div>

          {/* Formulario crear usuario */}
          {showCreateUser && (
            <form onSubmit={handleCreateUser} autoComplete="off" style={{
              background:'var(--surface)', border:'1px solid var(--border)',
              borderRadius:'var(--r-lg)', padding:'20px 24px', marginBottom:20,
              boxShadow:'var(--shadow)',
            }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:14 }}>
                NUEVO USUARIO
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:14 }}>
                <Field label="Nombre">
                  <input
                    value={createUserForm.nombre}
                    onChange={e => setCreateUserForm(f => ({ ...f, nombre: e.target.value }))}
                    style={inputStyle} placeholder="Juan García"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email" value={createUserForm.email}
                    onChange={e => setCreateUserForm(f => ({ ...f, email: e.target.value }))}
                    style={inputStyle} placeholder="juan@email.com"
                  />
                </Field>
                <Field label="Contraseña">
                  <input
                    type="password" value={createUserForm.password}
                    onChange={e => setCreateUserForm(f => ({ ...f, password: e.target.value }))}
                    autoComplete="new-password"
                    style={inputStyle} placeholder="Mínimo 6 caracteres"
                  />
                </Field>
                <Field label="Rol">
                  <select
                    value={createUserForm.rol}
                    onChange={e => setCreateUserForm(f => ({ ...f, rol: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="user">Usuario</option>
                    <option value="admin">Admin</option>
                  </select>
                </Field>
              </div>
              {createUserError && (
                <div style={{ marginTop:12, color:'#ef4444', fontSize:13 }}>{createUserError}</div>
              )}
              <button type="submit" disabled={createUserSaving} style={{
                marginTop:16, padding:'10px 24px', borderRadius:'var(--r-full)',
                background:'var(--green)', color:'#fff', border:'none',
                fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
                cursor: createUserSaving ? 'not-allowed' : 'pointer',
                opacity: createUserSaving ? 0.7 : 1,
              }}>
                {createUserSaving ? 'Creando...' : 'Crear Usuario'}
              </button>
            </form>
          )}

          {/* Filtro tenant */}
          <div style={{ display:'flex', gap:6, marginBottom:12 }}>
            {[
              { id:'todos',       label:'Todos' },
              { id:'con-tenant',  label:'Con tenant' },
              { id:'sin-tenant',  label:'Sin tenant' },
            ].map(f => (
              <button key={f.id} onClick={() => setFiltroTenant(f.id)} style={{
                padding:'5px 14px', borderRadius:'var(--r-full)',
                fontSize:12, fontWeight:600, cursor:'pointer',
                border: filtroTenant === f.id ? '2px solid var(--green)' : '1px solid var(--border)',
                background: filtroTenant === f.id ? 'var(--green-bg)' : 'transparent',
                color: filtroTenant === f.id ? 'var(--green-light)' : 'var(--text-muted)',
                fontFamily:'var(--font-body)',
              }}>{f.label}</button>
            ))}
          </div>

          {loadingUsuarios ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:14 }}>Cargando...</div>
          ) : visibleUsuarios.length === 0 ? (
            <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:14 }}>
              {filtroTenant === 'todos' ? 'No hay usuarios.' : 'No hay usuarios con ese filtro.'}
            </div>
          ) : (
            <div className="scroll-x">
            <div className="scroll-x-inner" style={{
              background:'var(--surface)', border:'1px solid var(--border)',
              borderRadius:'var(--r-lg)', overflow:'hidden', boxShadow:'var(--shadow)',
            }}>
              {/* Encabezados */}
              <div style={{
                display:'grid', gridTemplateColumns:'1fr 1fr 210px',
                gap:12, padding:'10px 20px', borderBottom:'1px solid var(--border)',
                fontSize:10, fontWeight:700, letterSpacing:'.07em', color:'var(--text-muted)',
              }}>
                <div>USUARIO</div><div>TENANT ASIGNADO</div><div />
              </div>

              {visibleUsuarios.map((u, i) => {
                const tenantAsignado = tenants.find(t => t.user_id === u.id);
                const isEditing = assigningId === u.id;
                const displayName = u.nombre || u.username || u.email || u.id.slice(0, 8);

                return (
                  <div key={u.id} style={{
                    borderBottom: i < visibleUsuarios.length - 1 ? '1px solid var(--border)' : 'none',
                    padding:'14px 20px',
                  }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 210px', gap:12, alignItems:'center' }}>
                      {/* Nombre usuario */}
                      <div>
                        <div style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)' }}>{displayName}</div>
                        {u.email && u.nombre && (
                          <div style={{ fontSize:12, color:'var(--text-muted)' }}>{u.email}</div>
                        )}
                      </div>

                      {/* Tenant asignado */}
                      <div>
                        {tenantAsignado ? (
                          <span style={{ fontSize:13, color:'var(--text-primary)', fontWeight:500 }}>
                            {tenantAsignado.emoji} {tenantAsignado.nombre}
                          </span>
                        ) : (
                          <span style={{ fontSize:12, color:'var(--text-muted)' }}>Sin tenant</span>
                        )}
                      </div>

                      {/* Acciones */}
                      <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                        <button onClick={() => {
                          setAssigningId(isEditing ? null : u.id);
                          setAssignTenantId(tenantAsignado?.id || '');
                          setDeletingId(null);
                        }} style={smallBtnStyle}>
                          {isEditing ? 'Cancelar' : tenantAsignado ? 'Cambiar' : 'Asignar'}
                        </button>
                        <button onClick={() => { setDeletingId(deletingId === u.id ? null : u.id); setAssigningId(null); setDeleteError(''); }}
                          style={{ ...smallBtnStyle, color:'#ef4444', borderColor:'rgba(239,68,68,0.4)' }}>
                          {deletingId === u.id ? 'Cancelar' : 'Eliminar'}
                        </button>
                      </div>
                    </div>
                    {deletingId === u.id && (
                      <div style={{
                        marginTop:10, padding:'12px 16px',
                        background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)',
                        borderRadius:'var(--r-md)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
                      }}>
                        <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                          ¿Eliminar a <strong style={{ color:'var(--text-primary)' }}>{displayName}</strong>? Esta acción no se puede deshacer.
                          {deleteError && <span style={{ color:'#ef4444' }}> — {deleteError}</span>}
                        </div>
                        <button onClick={() => handleDeleteUser(u.id)} disabled={deleteSaving} style={{
                          padding:'8px 20px', borderRadius:'var(--r-full)', whiteSpace:'nowrap',
                          background:'#ef4444', color:'#fff', border:'none',
                          fontSize:13, fontWeight:600, fontFamily:'var(--font-body)',
                          cursor: deleteSaving ? 'not-allowed' : 'pointer', opacity: deleteSaving ? 0.7 : 1, flexShrink:0,
                        }}>
                          {deleteSaving ? 'Eliminando...' : 'Sí, eliminar'}
                        </button>
                      </div>
                    )}

                    {/* Selector de tenant (expandido) */}
                    {isEditing && (
                      <div style={{
                        marginTop:12, paddingTop:12, borderTop:'1px solid var(--border)',
                        display:'flex', alignItems:'center', gap:10,
                      }}>
                        <select
                          value={assignTenantId}
                          onChange={e => setAssignTenantId(e.target.value)}
                          style={{ ...inputStyle, maxWidth:280 }}
                        >
                          <option value="">— Sin tenant —</option>
                          {tenants.map(t => (
                            <option key={t.id} value={t.id}>
                              {t.emoji} {t.nombre} ({t.plan})
                              {t.user_id && t.user_id !== u.id ? ' — ya asignado' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleAssign(u.id)}
                          disabled={assignSaving}
                          style={{
                            padding:'9px 20px', borderRadius:'var(--r-full)',
                            background:'var(--green)', color:'#fff', border:'none',
                            fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
                            cursor: assignSaving ? 'not-allowed' : 'pointer',
                            opacity: assignSaving ? 0.7 : 1,
                          }}
                        >
                          {assignSaving ? 'Guardando...' : 'Confirmar'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
          )}

        </div>
      )}

      {/* ─── Tab: Tenants ──────────────────────────────────────────────────── */}
      {activeTab === 'tenants' && <>

      {/* Cabecera tenants */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
          TENANTS ({tenants.length})
        </div>
        <button
          onClick={() => { setShowForm(s => !s); resetForm(); }}
          style={{
            padding:'8px 18px', borderRadius:'var(--r-full)',
            background:'var(--green)', color:'#fff', border:'none',
            fontFamily:'var(--font-body)', fontSize:13, fontWeight:600, cursor:'pointer',
          }}
        >
          {showForm ? 'Cancelar' : '+ Nuevo Tenant'}
        </button>
      </div>

      {/* ─── Formulario ─────────────────────────────────────────────────────── */}
      {showForm && (
        <form onSubmit={handleSubmit} style={{
          background:'var(--surface)', border:'1px solid var(--border)',
          borderRadius:'var(--r-lg)', padding:'24px', marginBottom:20,
          boxShadow:'var(--shadow)',
        }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:16 }}>
            NUEVO TENANT
          </div>

          {/* Campos base */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:14 }}>
            <Field label="Nombre del negocio">
              <input value={form.nombre} onChange={e => handleNombreChange(e.target.value)} required style={inputStyle} placeholder="Ej: Los Manza" />
            </Field>
            <Field label="Slug (URL)">
              <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} required style={inputStyle} placeholder="los-manza" />
            </Field>
            <Field label="Emoji">
              <input value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} style={inputStyle} placeholder="📅" />
            </Field>
            <Field label="Tipo de negocio">
              <select value={form.tipo_negocio} onChange={e => handleTipoChange(e.target.value)} style={inputStyle}>
                <option value="">— Seleccionar —</option>
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Duración del turno (min)">
              <input type="number" value={form.duracion_turno} min={5} max={480} step={5}
                onChange={e => setForm(f => ({ ...f, duracion_turno: e.target.value }))}
                style={inputStyle} />
            </Field>
            <Field label="Plan">
              <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))} style={inputStyle}>
                {PLANES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:14 }}>
            <input type="checkbox" id="activo" checked={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} />
            <label htmlFor="activo" style={{ fontSize:13, color:'var(--text-secondary)', cursor:'pointer' }}>Activo</label>
          </div>

          {/* ─── Sección canchas (solo cancha_futbol) ──────────────────────── */}
          {esCancha && (
            <div style={{
              marginTop:24, paddingTop:20, borderTop:'1px solid var(--border)',
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
                  CANCHAS
                </div>
                <button type="button" onClick={addCancha} style={smallBtnStyle}>
                  + Agregar cancha
                </button>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {canchas.map((cancha, i) => (
                  <div key={i} style={{
                    display:'grid', gridTemplateColumns:'1fr 120px 36px',
                    gap:10, alignItems:'center',
                    background:'var(--bg3)', borderRadius:'var(--r-md)', padding:'12px 14px',
                  }}>
                    <input
                      value={cancha.nombre}
                      onChange={e => updateCancha(i, 'nombre', e.target.value)}
                      style={{ ...inputStyle, background:'var(--surface)' }}
                      placeholder={`Cancha ${i + 1}`}
                    />
                    <select
                      value={cancha.tamano}
                      onChange={e => updateCancha(i, 'tamano', e.target.value)}
                      style={{ ...inputStyle, background:'var(--surface)' }}
                    >
                      {TAMANOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button type="button" onClick={() => removeCancha(i)}
                      disabled={canchas.length === 1}
                      style={{
                        width:36, height:36, borderRadius:'var(--r-md)',
                        border:'1px solid var(--border)', background:'transparent',
                        color:'var(--text-muted)', cursor: canchas.length === 1 ? 'default' : 'pointer',
                        opacity: canchas.length === 1 ? 0.3 : 1, fontSize:16,
                      }}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              {/* Checkbox combinaciones */}
              <div style={{ marginTop:18 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <input
                    type="checkbox" id="tieneCombos"
                    checked={tieneCombinaciones}
                    onChange={e => setTieneCombinaciones(e.target.checked)}
                  />
                  <label htmlFor="tieneCombos" style={{ fontSize:13, color:'var(--text-secondary)', cursor:'pointer', fontWeight:600 }}>
                    ¿Las canchas se pueden combinar?
                  </label>
                </div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4, marginLeft:22 }}>
                  Si dos o más canchas se unen para formar una más grande, al reservarla se bloquean todas las que la componen.
                </div>
              </div>

              {/* Builder de combinaciones */}
              {tieneCombinaciones && (
                <div style={{ marginTop:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
                      COMBINACIONES
                    </div>
                    <button type="button" onClick={addCombinacion} style={smallBtnStyle}>
                      + Nueva combinación
                    </button>
                  </div>

                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    {combinaciones.map((combo, ci) => (
                      <div key={ci} style={{
                        background:'var(--bg3)', borderRadius:'var(--r-md)',
                        padding:'14px 16px', border:'1px solid var(--border)',
                      }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                          <input
                            value={combo.nombre}
                            onChange={e => updateComboNombre(ci, e.target.value)}
                            style={{ ...inputStyle, background:'var(--surface)', maxWidth:220 }}
                            placeholder={`Cancha grande ${ci + 1}`}
                          />
                          {combinaciones.length > 1 && (
                            <button type="button" onClick={() => removeCombinacion(ci)}
                              style={{
                                background:'transparent', border:'none',
                                color:'var(--text-muted)', cursor:'pointer', fontSize:13,
                              }}>
                              Quitar
                            </button>
                          )}
                        </div>
                        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:8 }}>
                          Canchas que la forman (mínimo 2):
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                          {canchas.map((cancha, ci2) => {
                            const selected = combo.canchaIndices.includes(ci2);
                            const label    = cancha.nombre.trim() || `Cancha ${ci2 + 1}`;
                            return (
                              <button
                                type="button" key={ci2}
                                onClick={() => toggleCanchaEnCombo(ci, ci2)}
                                style={{
                                  padding:'6px 14px', borderRadius:'var(--r-full)',
                                  border: selected ? '2px solid var(--green)' : '1px solid var(--border)',
                                  background: selected ? 'var(--green-bg)' : 'transparent',
                                  color: selected ? 'var(--green-light)' : 'var(--text-secondary)',
                                  fontSize:12, fontWeight:600, cursor:'pointer', transition:'all .15s',
                                }}
                              >
                                {label} ({cancha.tamano})
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {error && <div style={{ marginTop:14, color:'#ef4444', fontSize:13 }}>{error}</div>}

          <button type="submit" disabled={saving} style={{
            marginTop:20, padding:'10px 24px', borderRadius:'var(--r-full)',
            background:'var(--green)', color:'#fff', border:'none',
            fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Guardando...' : 'Crear Tenant'}
          </button>
        </form>
      )}

      {/* ─── Lista de tenants ─────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:14 }}>
          Cargando...
        </div>
      ) : tenants.length === 0 ? (
        <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:14 }}>
          No hay tenants todavía.
        </div>
      ) : (
        <div className="scroll-x">
        <div className="scroll-x-inner" style={{
          background:'var(--surface)', border:'1px solid var(--border)',
          borderRadius:'var(--r-lg)', overflow:'hidden', boxShadow:'var(--shadow)',
        }}>
          <div style={{
            display:'grid', gridTemplateColumns:'40px 1fr 140px 90px 110px 70px 100px',
            gap:12, padding:'10px 20px', borderBottom:'1px solid var(--border)',
            fontSize:10, fontWeight:700, letterSpacing:'.07em', color:'var(--text-muted)',
          }}>
            <div /><div>NEGOCIO</div><div>TIPO</div><div>DURACIÓN</div><div>PLAN</div><div>ESTADO</div><div />
          </div>
          {tenants.map((t, i) => (
            <div key={t.id} style={{
              borderBottom: i < tenants.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{
                display:'grid', gridTemplateColumns:'40px 1fr 140px 90px 110px 70px 100px',
                alignItems:'center', gap:12, padding:'14px 20px',
              }}>
                <div style={{ fontSize:20, textAlign:'center' }}>{t.emoji || '📅'}</div>
                <div>
                  <div style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)' }}>{t.nombre}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)' }}>/{t.slug}</div>
                </div>
                <div style={{ fontSize:12, color:'var(--text-secondary)' }}>
                  {TIPOS.find(tp => tp.value === t.tipo_negocio)?.label || '—'}
                </div>
                <div style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:500 }}>
                  {t.duracion_turno || 30} min
                </div>
                <div>
                  <span style={{
                    background:'var(--green-bg)', border:'1px solid var(--green-border)',
                    color:'var(--green-light)', borderRadius:'var(--r-full)',
                    padding:'2px 10px', fontSize:11, fontWeight:700,
                  }}>
                    {t.plan}
                  </span>
                </div>
                <div style={{ fontSize:12, fontWeight:600, color: t.activo ? 'var(--green-light)' : 'var(--text-muted)' }}>
                  {t.activo ? 'Activo' : 'Inactivo'}
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <button onClick={() => { setDeletingTenantId(deletingTenantId === t.id ? null : t.id); setDeleteTenantError(''); }}
                    style={{ ...smallBtnStyle, color:'#ef4444', borderColor:'rgba(239,68,68,0.4)' }}>
                    {deletingTenantId === t.id ? 'Cancelar' : 'Eliminar'}
                  </button>
                </div>
              </div>
              {deletingTenantId === t.id && (
                <div style={{
                  margin:'0 20px 14px', padding:'12px 16px',
                  background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)',
                  borderRadius:'var(--r-md)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
                }}>
                  <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                    ¿Eliminar <strong style={{ color:'var(--text-primary)' }}>{t.nombre}</strong> y todos sus datos? Esta acción no se puede deshacer.
                    {deleteTenantError && <span style={{ color:'#ef4444' }}> — {deleteTenantError}</span>}
                  </div>
                  <button onClick={() => handleDeleteTenant(t.id)} disabled={deleteTenantSaving} style={{
                    padding:'8px 20px', borderRadius:'var(--r-full)', whiteSpace:'nowrap',
                    background:'#ef4444', color:'#fff', border:'none',
                    fontSize:13, fontWeight:600, fontFamily:'var(--font-body)',
                    cursor: deleteTenantSaving ? 'not-allowed' : 'pointer',
                    opacity: deleteTenantSaving ? 0.7 : 1, flexShrink:0,
                  }}>
                    {deleteTenantSaving ? 'Eliminando...' : 'Sí, eliminar'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        </div>
      )}

      </>}
    </div>
  );
}

const inputStyle = {
  width:'100%', padding:'9px 12px', borderRadius:'var(--r-md)',
  border:'1px solid var(--border)', background:'var(--bg3)',
  color:'var(--text-primary)', fontFamily:'var(--font-body)', fontSize:13,
  boxSizing:'border-box',
};

const smallBtnStyle = {
  padding:'6px 14px', borderRadius:'var(--r-full)',
  border:'1px solid var(--border)', background:'transparent',
  color:'var(--text-secondary)', fontFamily:'var(--font-body)',
  fontSize:12, fontWeight:600, cursor:'pointer',
};

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.06em', color:'var(--text-muted)', marginBottom:6 }}>
        {label.toUpperCase()}
      </div>
      {children}
    </div>
  );
}
